import { Spotlight, spotlight, type SpotlightActionData, type SpotlightActionGroupData } from '@mantine/spotlight';
import { useDebouncedValue } from '@mantine/hooks';
import {
  IconAdjustments,
  IconArrowRight,
  IconBeach,
  IconBell,
  IconBriefcase,
  IconBuilding,
  IconBuildingFactory2,
  IconCalendarStats,
  IconCalendarTime,
  IconCar,
  IconCategory,
  IconChartBar,
  IconChecklist,
  IconClipboardList,
  IconClock,
  IconCrown,
  IconDeviceMobile,
  IconFiles,
  IconHelp,
  IconHistory,
  IconHome,
  IconKey,
  IconLanguage,
  IconLayoutKanban,
  IconLicense,
  IconLifebuoy,
  IconPhoto,
  IconPlug,
  IconPlus,
  IconReceipt,
  IconRocket,
  IconSearch,
  IconSettings,
  IconShieldCheck,
  IconShieldHalf,
  IconTicket,
  IconTool,
  IconUser,
  IconUserCircle,
  IconUsers,
} from '@tabler/icons-react';
import { useNavigate } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState, useSyncExternalStore, type ReactNode } from 'react';
import { fetchList } from '@/shared/api/jsonapi/client';
import type { Denormalized } from '@/shared/api/jsonapi/parser';
import { getAllSearchEntries, subscribeSearchRegistry, type SearchEntry } from '@/shared/lib/searchRegistry';

const EMPTY_ENTRIES: SearchEntry[] = [];

const RECENT_KEY = 'spotlight-recent';
const RECENT_MAX = 5;

interface RecentEntry { id: string; label: string; to: string; iconName?: string }

function loadRecent(): RecentEntry[] {
  try { return JSON.parse(localStorage.getItem(RECENT_KEY) ?? '[]'); } catch { return []; }
}
function pushRecent(entry: RecentEntry) {
  try {
    const list = loadRecent().filter((e) => e.to !== entry.to);
    list.unshift(entry);
    localStorage.setItem(RECENT_KEY, JSON.stringify(list.slice(0, RECENT_MAX)));
  } catch { /* ignore */ }
}

interface NavSearch extends Denormalized { type: string; title?: string; name?: string; firstName?: string; lastName?: string; email?: string }

export function SpotlightProvider({ children }: { children: ReactNode }) {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [debouncedQuery] = useDebouncedValue(query, 250);
  const [recent, setRecent] = useState<RecentEntry[]>([]);

  useEffect(() => { setRecent(loadRecent()); }, []);

  // Subscribe al registry dinamico: ogni pagina che usa `useRegisterSearch`
  // inietta voci visibili qui (gruppo "Su questa pagina").
  const dynamicEntries = useSyncExternalStore<SearchEntry[]>(
    subscribeSearchRegistry,
    getAllSearchEntries,
    () => EMPTY_ENTRIES,
  );

  const go = useCallback((to: string, label: string) => {
    spotlight.close();
    pushRecent({ id: to, to, label });
    setRecent(loadRecent());
    void navigate({ to });
  }, [navigate]);

  // Live search: tasks / projects / users via API (best-effort; failures = empty)
  const enabled = debouncedQuery.trim().length >= 2;
  const tasksQ = useQuery({
    queryKey: ['spotlight-tasks', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('tasks', {
          filter: { title: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });
  const projectsQ = useQuery({
    queryKey: ['spotlight-projects', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('project-entities', {
          filter: { name: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });
  const usersQ = useQuery({
    queryKey: ['spotlight-users', debouncedQuery],
    enabled,
    staleTime: 30_000,
    queryFn: async () => {
      try {
        const res = await fetchList<NavSearch>('users', {
          filter: { email: { like: debouncedQuery } },
          page: { size: 5 },
        });
        return res.data;
      } catch { return []; }
    },
  });

  const actionGroups = useMemo<SpotlightActionGroupData[]>(() => {
    const groups: SpotlightActionGroupData[] = [];

    // QUICK ACTIONS — sempre in cima
    groups.push({
      group: 'Azioni rapide',
      actions: [
        {
          id: 'qa-new-task',
          label: 'Nuovo task',
          description: 'Apri il form di creazione task',
          leftSection: <IconPlus size={18} />,
          keywords: ['task', 'nuovo', 'crea', 'create'],
          onClick: () => {
            spotlight.close();
            void navigate({ to: '/tasks' });
            // I componenti di lista mostrano il pulsante "Nuovo": rinvio l'utente lì.
          },
        },
        {
          id: 'qa-clock',
          label: 'Timbratura',
          description: 'Apri il widget timbratura nella home',
          leftSection: <IconClock size={18} />,
          keywords: ['clock', 'timbra', 'attendance', 'presenza'],
          onClick: () => go('/', 'Home'),
        },
        {
          id: 'qa-ticket',
          label: 'Nuovo ticket',
          description: 'Apri il centro assistenza',
          leftSection: <IconTicket size={18} />,
          keywords: ['ticket', 'help', 'supporto'],
          onClick: () => go('/tickets', 'Help desk'),
        },
      ],
    });

    // RECENT
    if (recent.length > 0) {
      groups.push({
        group: 'Recenti',
        actions: recent.map((r) => ({
          id: `recent-${r.id}`,
          label: r.label,
          leftSection: <IconHistory size={18} />,
          onClick: () => go(r.to, r.label),
        })),
      });
    }

    // ENTRIES REGISTRATE DALLA PAGINA CORRENTE — bottoni, sezioni, titoli
    // esposti dalle pagine via `useRegisterSearch`.
    if (dynamicEntries.length > 0) {
      // Raggruppo per `group` (default "Su questa pagina")
      const byGroup = new Map<string, SearchEntry[]>();
      for (const e of dynamicEntries) {
        const g = e.group ?? 'Su questa pagina';
        const arr = byGroup.get(g) ?? [];
        arr.push(e);
        byGroup.set(g, arr);
      }
      for (const [groupName, entries] of byGroup) {
        groups.push({
          group: groupName,
          actions: entries.map((e) => ({
            id: `reg-${e.id}`,
            label: e.label,
            description: e.description,
            leftSection: <IconArrowRight size={18} />,
            keywords: e.keywords,
            onClick: () => {
              if (e.onSelect) {
                spotlight.close();
                e.onSelect();
              } else if (e.to) {
                go(e.to, e.label);
              }
            },
          })),
        });
      }
    }

    // SEARCH RESULTS (only when typing >= 2)
    if (enabled) {
      const taskActions: SpotlightActionData[] = (tasksQ.data ?? []).map((t) => ({
        id: `task-${t.id}`,
        label: t.title ?? '(task senza titolo)',
        description: `Task · ${t.id.slice(0, 8)}`,
        leftSection: <IconClipboardList size={18} />,
        keywords: ['task', t.title ?? ''].filter(Boolean),
        onClick: () => go(`/tasks`, t.title ?? 'Task'),
      }));
      const projActions: SpotlightActionData[] = (projectsQ.data ?? []).map((p) => ({
        id: `project-${p.id}`,
        label: p.name ?? '(commessa senza nome)',
        description: `Commessa · ${p.id.slice(0, 8)}`,
        leftSection: <IconBuildingFactory2 size={18} />,
        onClick: () => go(`/projects/${p.id}`, p.name ?? 'Commessa'),
      }));
      const userActions: SpotlightActionData[] = (usersQ.data ?? []).map((u) => {
        const full = [u.firstName, u.lastName].filter(Boolean).join(' ') || u.email || '(utente)';
        return {
          id: `user-${u.id}`,
          label: full,
          description: u.email ?? `Utente · ${u.id.slice(0, 8)}`,
          leftSection: <IconUsers size={18} />,
          onClick: () => go(`/users/${u.id}`, full),
        };
      });
      if (taskActions.length) groups.push({ group: 'Task', actions: taskActions });
      if (projActions.length) groups.push({ group: 'Commesse', actions: projActions });
      if (userActions.length) groups.push({ group: 'Utenti', actions: userActions });
    }

    // STATIC NAV
    groups.push({
      group: 'Navigazione',
      actions: STATIC_ROUTES.map((r) => ({
        id: `nav-${r.to}`,
        label: r.label,
        description: r.section,
        leftSection: <r.icon size={18} />,
        keywords: r.keywords,
        onClick: () => go(r.to, r.label),
      })),
    });

    return groups;
  }, [recent, enabled, tasksQ.data, projectsQ.data, usersQ.data, dynamicEntries, navigate, go]);

  return (
    <>
      {children}
      <Spotlight
        actions={actionGroups}
        nothingFound="Nessun risultato"
        highlightQuery
        searchProps={{
          leftSection: <IconSearch size={20} />,
          placeholder: 'Cerca pagine, task, commesse, utenti…',
          value: query,
          onChange: (e) => setQuery(e.currentTarget.value),
        }}
        shortcut={['mod + K', 'mod + P', '/']}
        scrollable
        maxHeight={520}
      />
    </>
  );
}

export interface StaticRoute {
  to: string;
  label: string;
  section: string;
  icon: typeof IconHome;
  keywords?: string[];
}

/** Catalogo statico delle pagine/rotte dell'applicativo (riusato anche dalla
 *  gestione tenant → tab "Rotte"). */
export const STATIC_ROUTES: StaticRoute[] = [
  { to: '/', label: 'Home', section: 'Principale', icon: IconHome, keywords: ['dashboard', 'inizio'] },

  // Operativo
  { to: '/projects', label: 'Commesse', section: 'Operativo', icon: IconBuildingFactory2, keywords: ['progetti', 'commesse', 'lavori'] },
  { to: '/projects/new', label: 'Nuova commessa', section: 'Operativo', icon: IconPlus, keywords: ['crea', 'aggiungi', 'new project'] },
  { to: '/projects/gantt', label: 'Commesse — Gantt', section: 'Operativo', icon: IconCalendarTime, keywords: ['gantt', 'timeline'] },
  { to: '/project-forecast', label: 'Forecast commesse', section: 'Operativo', icon: IconChartBar, keywords: ['previsione', 'forecast'] },
  { to: '/tasks', label: 'Task — Lista', section: 'Operativo', icon: IconClipboardList, keywords: ['attività', 'todo'] },
  { to: '/tasks/kanban', label: 'Task — Kanban', section: 'Operativo', icon: IconLayoutKanban, keywords: ['board', 'kanban'] },
  { to: '/tasks/gantt', label: 'Task — Gantt', section: 'Operativo', icon: IconCalendarTime, keywords: ['gantt', 'timeline'] },
  { to: '/timesheet', label: 'Timesheet', section: 'Operativo', icon: IconClock, keywords: ['ore', 'rendicontazione'] },
  { to: '/timesheet/approval', label: 'Timesheet — Approvazione', section: 'Operativo', icon: IconChecklist, keywords: ['approva', 'firmare'] },
  { to: '/timesheet/by-project', label: 'Timesheet per commessa', section: 'Operativo', icon: IconClock, keywords: ['report ore'] },
  { to: '/timesheet/reasons', label: 'Causali timesheet', section: 'Operativo', icon: IconAdjustments },
  { to: '/planning', label: 'Pianificazione', section: 'Operativo', icon: IconCalendarStats, keywords: ['planning', 'schedula'] },
  { to: '/quality', label: 'Qualità', section: 'Operativo', icon: IconShieldCheck, keywords: ['quality', 'iso'] },
  { to: '/products', label: 'Prodotti', section: 'Operativo', icon: IconCategory },
  { to: '/intercompany', label: 'Intercompany', section: 'Operativo', icon: IconBuildingFactory2 },

  // Mezzi
  { to: '/vehicles', label: 'Parco mezzi', section: 'Mezzi', icon: IconCar, keywords: ['auto', 'macchine', 'veicoli'] },
  { to: '/instruments-calendar', label: 'Prenotazioni mezzi', section: 'Mezzi', icon: IconCalendarTime, keywords: ['auto', 'prenota'] },
  { to: '/car-history', label: 'Storico mezzi', section: 'Mezzi', icon: IconHistory, keywords: ['rilascio', 'consegna'] },
  { to: '/secretary/area', label: 'Area Segreteria', section: 'Mezzi', icon: IconBriefcase },

  // HR
  { to: '/attendance', label: 'Presenze', section: 'HR', icon: IconUserCircle, keywords: ['timbra', 'badge', 'attendance'] },
  { to: '/leave', label: 'Ferie / Permessi', section: 'HR', icon: IconBeach, keywords: ['ferie', 'permessi', 'assenze'] },
  { to: '/smart-working', label: 'Smart working', section: 'HR', icon: IconChecklist, keywords: ['sw', 'remote', 'home working'] },
  { to: '/workforce', label: 'Workforce', section: 'HR', icon: IconBriefcase, keywords: ['dipendenti', 'staff'] },
  { to: '/workforce/dashboard', label: 'Workforce — Dashboard', section: 'HR', icon: IconChartBar },
  { to: '/workforce/today', label: 'Chi c\'è oggi', section: 'HR', icon: IconUsers, keywords: ['presenze', 'team', 'oggi', 'coverage', 'assenti'] },
  { to: '/workforce/areas', label: 'Workforce — Aree', section: 'HR', icon: IconBuilding },
  { to: '/workforce/sites', label: 'Workforce — Cantieri', section: 'HR', icon: IconBuildingFactory2, keywords: ['siti', 'sites'] },
  { to: '/workforce/site-presences', label: 'Presenze in cantiere', section: 'HR', icon: IconUsers },
  { to: '/workforce/groups', label: 'Workforce — Gruppi', section: 'HR', icon: IconUsers },
  { to: '/workforce/roles', label: 'Workforce — Mansioni', section: 'HR', icon: IconShieldCheck },
  { to: '/workforce/external', label: 'Personale esterno', section: 'HR', icon: IconUsers },
  { to: '/workforce/training', label: 'Formazione', section: 'HR', icon: IconChecklist, keywords: ['training', 'corsi'] },

  // Analytics
  { to: '/insights', label: 'Insights', section: 'Analytics', icon: IconChartBar, keywords: ['kpi', 'analisi'] },
  { to: '/insights/monthly-invoices', label: 'Fatturato mensile', section: 'Analytics', icon: IconReceipt },
  { to: '/statistics', label: 'Statistiche', section: 'Analytics', icon: IconChartBar },
  { to: '/reporting', label: 'Reporting', section: 'Analytics', icon: IconReceipt, keywords: ['report', 'export'] },
  { to: '/maeworld', label: 'MaeWorld', section: 'Analytics', icon: IconChartBar },

  // Contenuti
  { to: '/translations', label: 'Traduzioni', section: 'Contenuti', icon: IconLanguage, keywords: ['lingue', 'i18n'] },
  { to: '/templates', label: 'Template', section: 'Contenuti', icon: IconFiles },
  { to: '/media', label: 'Media', section: 'Contenuti', icon: IconPhoto, keywords: ['immagini', 'file'] },
  { to: '/articles', label: 'Articoli & guide', section: 'Contenuti', icon: IconHelp, keywords: ['knowledge', 'kb'] },

  // Supporto
  { to: '/tickets', label: 'Help desk', section: 'Supporto', icon: IconTicket, keywords: ['ticket', 'supporto'] },
  { to: '/help', label: 'Aiuto & FAQ', section: 'Supporto', icon: IconHelp },
  { to: '/user-guide', label: 'Guida utente', section: 'Supporto', icon: IconHelp },
  { to: '/developer-guide', label: 'Guida sviluppatore', section: 'Supporto', icon: IconTool },
  { to: '/changelog', label: 'Changelog', section: 'Supporto', icon: IconHistory },
  { to: '/about', label: 'Chi siamo', section: 'Supporto', icon: IconHelp },
  { to: '/legals', label: 'Documenti legali', section: 'Supporto', icon: IconFiles, keywords: ['privacy', 'termini'] },

  // Admin
  { to: '/users', label: 'Utenti', section: 'Admin', icon: IconUsers },
  { to: '/users/new', label: 'Nuovo utente', section: 'Admin', icon: IconPlus, keywords: ['crea utente', 'invita'] },
  { to: '/roles', label: 'Ruoli', section: 'Admin', icon: IconShieldCheck },
  { to: '/admin/working-hours', label: 'Orari di lavoro', section: 'Admin', icon: IconClock },
  { to: '/admin/categories', label: 'Categorie', section: 'Admin', icon: IconCategory },
  { to: '/admin/structure-labels', label: 'Etichette struttura', section: 'Admin', icon: IconLanguage },
  { to: '/admin/translate-structure', label: 'Struttura traduzioni', section: 'Admin', icon: IconLanguage },
  { to: '/admin/tenants', label: 'Tenant', section: 'Admin', icon: IconBuilding, keywords: ['organizzazione', 'azienda'] },
  { to: '/admin/branding', label: 'Branding', section: 'Admin', icon: IconPhoto, keywords: ['logo', 'colore'] },
  { to: '/licensing', label: 'Licenze & piano', section: 'Admin', icon: IconLicense },
  { to: '/billing', label: 'Fatturazione', section: 'Admin', icon: IconReceipt },
  { to: '/admin/system', label: 'Manutenzione sistema', section: 'Admin', icon: IconTool },
  { to: '/admin/audit-log', label: 'Audit log', section: 'Admin', icon: IconShieldHalf, keywords: ['log', 'sicurezza'] },
  { to: '/admin/cross-tenant-access-logs', label: 'Audit cross-tenant', section: 'Admin', icon: IconShieldHalf, keywords: ['log', 'cross-tenant', 'audit', 'sotto-tenant'] },
  { to: '/admin/help-desk', label: 'Help Desk Admin', section: 'Admin', icon: IconLifebuoy },
  { to: '/setup', label: 'Onboarding wizard', section: 'Admin', icon: IconRocket },

  // SuperAdmin
  { to: '/admin/license-dashboard', label: 'Cruscotto licenze', section: 'SuperAdmin', icon: IconCrown },
  { to: '/admin/licenses-catalog', label: 'Catalogo licenze', section: 'SuperAdmin', icon: IconLicense },
  { to: '/admin/license-plans', label: 'Piani commerciali', section: 'SuperAdmin', icon: IconLicense },
  { to: '/admin/send-communications', label: 'Invia comunicazioni', section: 'SuperAdmin', icon: IconCrown },

  // Account
  { to: '/settings', label: 'Impostazioni', section: 'Account', icon: IconSettings },
  { to: '/settings/profile', label: 'Profilo', section: 'Account', icon: IconUser },
  { to: '/settings/preferences', label: 'Preferenze', section: 'Account', icon: IconAdjustments },
  { to: '/settings/notification-preferences', label: 'Preferenze notifiche', section: 'Account', icon: IconBell },
  { to: '/settings/security', label: 'Sicurezza', section: 'Account', icon: IconShieldHalf, keywords: ['password', '2fa'] },
  { to: '/settings/devices', label: 'Dispositivi', section: 'Account', icon: IconDeviceMobile },
  { to: '/settings/privacy', label: 'Privacy / GDPR', section: 'Account', icon: IconShieldCheck },
  { to: '/settings/api-keys', label: 'API Keys', section: 'Account', icon: IconKey },
  { to: '/settings/integrations', label: 'Integrazioni', section: 'Account', icon: IconPlug },
];
